import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, IGenericModal } from 'components/Modal/Modal';
import { switchCompany } from 'api/companyContext';
import { companyKeys } from 'api/queryKeys';
import { useMainCompaniesQuery } from 'hooks/queries/useMainCompaniesQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { ICompany } from 'types/company';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/Flex';
import { P } from 'components/Typography/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { useAccount } from 'hooks/useAccount';
import {
  CompanyList,
  CompanyItem,
  CompanyName,
  CompanyInfo,
  DefaultBadge,
  SelectedIcon,
  EmptyState
} from './CompanySelectModal.styled';

interface ICompanySelectModal extends IGenericModal {
  onCompanySwitched?: () => void;
}

interface CompanyListItemProps {
  company: ICompany;
  isDefault: boolean;
  isSelected: boolean;
  onSelect: (companyId: string) => void;
  defaultCompanyLabel: string;
}

const CompanyListItem: React.FC<CompanyListItemProps> = ({
  company,
  isDefault,
  isSelected,
  onSelect,
  defaultCompanyLabel
}) => {
  const companyId = company.id || '';

  return (
    <CompanyItem
      onClick={() => onSelect(companyId)}
      isSelected={isSelected}
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex flexDirection="column" flex="1">
        <Flex alignItems="center">
          <CompanyName variant="body">{company.full_name}</CompanyName>
          {isDefault && (
            <DefaultBadge variant="body">{defaultCompanyLabel}</DefaultBadge>
          )}
        </Flex>
        {company.short_name && (
          <CompanyInfo variant="body">{company.short_name}</CompanyInfo>
        )}
      </Flex>
      {isSelected && <SelectedIcon icon={faCheck} />}
    </CompanyItem>
  );
};

export const CompanySelectModal: React.FC<ICompanySelectModal> = ({
  isOpen,
  onCancelClick,
  onCompanySwitched
}) => {
  const { t } = useTranslation();
  const { loggedUserData } = useAccount();
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const { data: companiesData = [], isLoading: companiesLoading } = useMainCompaniesQuery(isOpen);
  const companies = useMemo<ICompany[]>(
    () => [...(companiesData ?? [])].sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? '')),
    [companiesData]
  );
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  const isLoading = companiesLoading;

  const defaultCompanyId = loggedUserData.companyId || '';

  useEffect(() => {
    if (isOpen && mainCompany?.id !== undefined) {
      setCurrentCompanyId(mainCompany.id ?? defaultCompanyId);
    }
  }, [isOpen, mainCompany?.id, defaultCompanyId]);

  const handleCompanySelect = useCallback(
    async (companyId: string) => {
      if (isLoading || companyId === currentCompanyId) return;

      try {
        await switchCompany(companyId);
        queryClient.invalidateQueries({ queryKey: companyKeys.main() });
        setCurrentCompanyId(companyId);
        onCancelClick();
        if (onCompanySwitched) {
          onCompanySwitched();
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error switching company:', error);
      }
    },
    [isLoading, currentCompanyId, onCancelClick, onCompanySwitched, queryClient]
  );

  const renderContent = () => {
    if (companies.length === 0) {
      return (
        <EmptyState>
          <FontAwesomeIcon icon={faBuilding} size="3x" style={{ opacity: 0.3, marginBottom: 16 }} />
          <P variant="body">{t('nav.noCompaniesAvailable')}</P>
        </EmptyState>
      );
    }

    return (
      <CompanyList>
        {companies.map((company) => {
          const companyId = company.id || '';
          return (
            <CompanyListItem
              key={company.id}
              company={company}
              isDefault={companyId === defaultCompanyId}
              isSelected={companyId === currentCompanyId}
              onSelect={handleCompanySelect}
              defaultCompanyLabel={t('nav.defaultCompany')}
            />
          );
        })}
      </CompanyList>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      headerTitle={t('nav.changeCompany')}
      mainButton={{
        action: onCancelClick,
        label: t('common.close'),
        variant: 'secondary'
      }}
      isSecondButtonVisible={false}
      isThirdButtonVisible={false}
      disableBodySharedStyles={true}
      bodyMaxHeight="60vh"
      gridTemplateColumns="40%"
    >
      {renderContent()}
    </Modal>
  );
};
